import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User,
  Calendar,
  Send,
  UserCheck,
  CheckCircle,
  Clock,
  Lock
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { usePermissions } from '@hooks/usePermissions';
import { ADMIN_PERMISSIONS, TICKET_STATUS, ADMIN_ROUTES } from '@constants/admin.constants';
import supportAdminService from '@services/admin/support.admin.service';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import PermissionGuard from '@components/admin/PermissionGuard';
import toast from 'react-hot-toast';

const SupportTicketDetails = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState('');

  useEffect(() => {
    if (!hasPermission(ADMIN_PERMISSIONS.VIEW_SUPPORT)) {
      toast.error('Access denied');
      navigate('/admin/dashboard');
      return;
    }
    fetchTicketDetails();
    fetchAdminList();
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    setLoading(true);
    try {
      const response = await supportAdminService.getTicketById(ticketId);
      if (response.success) {
        const ticketData = response?.data?.data;
        setTicket(ticketData);
        setSelectedStatus(ticketData?.status || '');
        setSelectedAdmin(ticketData?.assignedTo || '');
      }
    } catch (error) {
      toast.error('Failed to load ticket details');
      navigate(ADMIN_ROUTES.SUPPORT);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminList = async () => {
    try {
      const response = await supportAdminService.getAdminList();
      if (response.success) {
        setAdminList(Array.isArray(response.data?.data) ? response.data.data : []);
      }
    } catch (error) {
      console.error('Failed to load admin list');
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const response = await supportAdminService.addResponse(ticketId, message, isInternalNote);
      if (response.success) {
        toast.success(isInternalNote ? 'Internal note added' : 'Response sent');
        setMessage('');
        setIsInternalNote(false);
        fetchTicketDetails();
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (selectedStatus === ticket.status) {
      toast.error('Status unchanged');
      return;
    }

    try {
      const response = await supportAdminService.updateTicketStatus(ticketId, selectedStatus);
      if (response.success) {
        toast.success('Status updated successfully');
        fetchTicketDetails();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssignTicket = async () => {
    if (!selectedAdmin) {
      toast.error('Please select an admin');
      return;
    }

    if (selectedAdmin === ticket.assignedTo) {
      toast.error('Ticket already assigned to this admin');
      return;
    }

    try {
      const response = await supportAdminService.assignTicket(ticketId, selectedAdmin);
      if (response.success) {
        toast.success('Ticket assigned successfully');
        fetchTicketDetails();
      }
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      [TICKET_STATUS.OPEN]: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Open' },
      [TICKET_STATUS.IN_PROGRESS]: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'In Progress' },
      [TICKET_STATUS.WAITING_FOR_USER]: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Waiting for User' },
      [TICKET_STATUS.RESOLVED]: { bg: 'bg-green-100', text: 'text-green-700', label: 'Resolved' },
      [TICKET_STATUS.CLOSED]: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Closed' },
      [TICKET_STATUS.REOPENED]: { bg: 'bg-red-100', text: 'text-red-700', label: 'Reopened' },
    };
    const badge = badges[status] || badges[TICKET_STATUS.OPEN];
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (loading || !ticket) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <PermissionGuard requiredPermission={ADMIN_PERMISSIONS.VIEW_SUPPORT}>
      <div className="min-h-full bg-gray-50 p-3">
          <div className="mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ADMIN_ROUTES.SUPPORT)}
              className="mb-3"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Tickets
            </Button>
            <h1 className="text-2xl font-bold text-primary-900 mb-2">
              Ticket #{ticket.ticketNumber}
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <Card className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h2 className="text-xl font-bold text-primary-900 mb-2">
                      {ticket.subject}
                    </h2>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(ticket.status)}
                      <span className={`px-3 py-1 rounded text-xs font-semibold ${
                        ticket.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                        ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                        ticket.priority === 'medium' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {ticket.priority} Priority
                      </span>
                      <span className="text-sm text-primary-600">
                        {ticket.category}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-primary-900 whitespace-pre-wrap">
                    {ticket.description}
                  </p>
                </div>

                <div className="flex items-center gap-4 text-sm text-primary-600">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {ticket.user?.email}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(ticket.created_at).toLocaleString()}
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <h3 className="text-base font-bold text-primary-900 mb-3">
                  Conversation
                </h3>

                <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                  {ticket.responses && ticket.responses.length > 0 ? (
                    ticket.responses.map((response) => (
                      <div
                        key={response.id}
                        className={`p-3 rounded-lg ${
                          response.isInternalNote
                            ? 'bg-yellow-50 border border-yellow-200'
                            : response.user?.role === 'admin'
                            ? 'bg-blue-50 border border-blue-200'
                            : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                              {response.user?.email?.[0]?.toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-primary-900">
                                {response.user?.email}
                              </p>
                              <p className="text-xs text-primary-500">
                                {new Date(response.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          {response.isInternalNote && (
                            <span className="flex items-center gap-1 text-xs font-semibold text-yellow-700">
                              <Lock className="w-3 h-3" />
                              Internal Note
                            </span>
                          )}
                        </div>
                        <p className="text-primary-900 whitespace-pre-wrap">
                          {response.message}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-primary-500 py-8">
                      No responses yet
                    </p>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <label className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={isInternalNote}
                      onChange={(e) => setIsInternalNote(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-primary-900 flex items-center gap-1">
                      <Lock className="w-4 h-4" />
                      Internal Note (Only visible to admins)
                    </span>
                  </label>

                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={isInternalNote ? "Add internal note..." : "Type your response..."}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none mb-2"
                  />

                  <Button
                    variant="primary"
                    onClick={handleSendMessage}
                    disabled={sending || !message.trim()}
                    loading={sending}
                    className="w-full"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {isInternalNote ? 'Add Internal Note' : 'Send Response'}
                  </Button>
                </div>
              </Card>
            </div>

            <div className="space-y-4">
              <Card className="p-4">
                <h3 className="text-base font-bold text-primary-900 mb-3">
                  Ticket Management
                </h3>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-primary-700 mb-2">
                      Status
                    </label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                      <option value={TICKET_STATUS.OPEN}>Open</option>
                      <option value={TICKET_STATUS.IN_PROGRESS}>In Progress</option>
                      <option value={TICKET_STATUS.WAITING_FOR_USER}>Waiting for User</option>
                      <option value={TICKET_STATUS.RESOLVED}>Resolved</option>
                      <option value={TICKET_STATUS.CLOSED}>Closed</option>
                    </select>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleUpdateStatus}
                      disabled={selectedStatus === ticket.status}
                      className="w-full mt-2"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Update Status
                    </Button>
                  </div>

                  {hasPermission(ADMIN_PERMISSIONS.ASSIGN_TICKETS) && (
                    <div>
                      <label className="block text-sm font-semibold text-primary-700 mb-2">
                        Assign To
                      </label>
                      <select
                        value={selectedAdmin}
                        onChange={(e) => setSelectedAdmin(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      >
                        <option value="">Unassigned</option>
                        {adminList.map((admin) => (
                          <option key={admin.id} value={admin.id}>
                            {admin.email}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAssignTicket}
                        disabled={selectedAdmin === ticket.assignedTo}
                        className="w-full mt-2"
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        Assign Ticket
                      </Button>
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-4">
                <h3 className="text-base font-bold text-primary-900 mb-3">
                  Ticket Information
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-primary-600 mb-1">Ticket Number</p>
                    <p className="font-medium text-primary-900">{ticket.ticketNumber}</p>
                  </div>
                  <div>
                    <p className="text-primary-600 mb-1">Category</p>
                    <p className="font-medium text-primary-900 capitalize">{ticket.category}</p>
                  </div>
                  <div>
                    <p className="text-primary-600 mb-1">Created</p>
                    <p className="font-medium text-primary-900">
                      {new Date(ticket.created_at).toLocaleString()}
                    </p>
                  </div>
                  {ticket.resolvedAt && (
                    <div>
                      <p className="text-primary-600 mb-1">Resolved</p>
                      <p className="font-medium text-primary-900">
                        {new Date(ticket.resolved_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-primary-600 mb-1">Last Updated</p>
                    <p className="font-medium text-primary-900">
                      {new Date(ticket.updatedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
      </div>
    </PermissionGuard>
  );
};

export default SupportTicketDetails;
